import axios from 'axios';
import authService from './authService';
import { API_URL } from '../config';
import { encryptBody, decryptBody } from './cryptoService';

// API Base URL - merkezi config'den alınıyor
const API_BASE_URL = API_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - Add auth token, encrypt body
api.interceptors.request.use(
  async (config) => {
    // Skip auth check for public auth endpoints
    if (config.url?.includes('/auth/login') || config.url?.includes('/auth/register') || config.url?.includes('/auth/signup') || (config.url?.startsWith('/customers') && !config.url?.includes('/send-email'))) {
      console.log('🔐 API: Skipping auth check for login endpoint');
      
      // Login/register da encrypt edilecek (body varsa)
      if (config.data && ['post', 'put', 'patch'].includes(config.method || '')) {
        try {
          const encrypted = await encryptBody(config.data);
          config.data = encrypted;
          config.headers['X-Encrypted'] = 'true';
        } catch (e) {
          console.warn('⚠️ Encryption failed, sending plain:', e);
        }
      }
      
      return config;
    }
    
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      console.log('🔐 API: User not authenticated, redirecting to login');
      // Don't redirect for login endpoint
      if (!config.url?.includes('/auth/login')) {
        window.location.href = '/login';
      }
      return Promise.reject(new Error('Authentication failed'));
    }

    const accessToken = authService.getAccessToken();
    console.log('🔐 API: Request interceptor', {
      url: config.url,
      accessToken: accessToken,
      tokenLength: accessToken?.length,
      willAddHeader: !!(accessToken && accessToken !== 'null' && accessToken !== 'undefined')
    });
    
    if (config.url?.startsWith('/customers') && !config.url?.includes('/send-email')) {
      // Public customers fetch: don't add auth header to avoid preflight/401
      delete config.headers.Authorization;
    } else if (accessToken && accessToken !== 'null' && accessToken !== 'undefined' && accessToken.length > 10) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      console.log('🔐 API: Added Authorization header:', `Bearer ${accessToken.substring(0, 20)}...`);
    } else {
      console.log('🔐 API: No valid token found, skipping auth header');
      // Don't add Authorization header if token is invalid
      delete config.headers.Authorization;
    }
    
    // 🔒 Body encrypt (POST, PUT, PATCH)
    if (config.data && ['post', 'put', 'patch'].includes(config.method || '')) {
      // FormData veya Blob ise encrypt etme (dosya yükleme)
      if (config.data instanceof FormData || config.data instanceof Blob) {
        console.log('🔒 API: Skipping encryption for FormData/Blob');
        delete config.headers['Content-Type'];
      } else {
        try {
          const encrypted = await encryptBody(config.data);
          config.data = encrypted;
          config.headers['X-Encrypted'] = 'true';
        } catch (e) {
          console.warn('⚠️ Encryption failed, sending plain:', e);
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Decrypt encrypted responses, handle errors and token refresh
api.interceptors.response.use(
  async (response) => {
    // 🔓 Response decrypt - body {iv, data} formatındaysa şifreli demektir
    if (response.data && typeof response.data === 'object' && response.data.iv && response.data.data && Object.keys(response.data).length === 2) {
      try {
        response.data = await decryptBody(response.data.iv, response.data.data);
      } catch (e) {
        console.warn('⚠️ Response decrypt failed:', e);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Check for malformed token
      if (error.response?.data?.code === 'TOKEN_MALFORMED') {
        console.log('🔐 API: Malformed token detected, clearing storage');
        await authService.logout();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Try to refresh token
      const refreshed = await authService.refreshAccessToken();
      if (refreshed) {
        // Retry the original request with new token
        const newToken = authService.getAccessToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      }

      // Refresh failed, redirect to login
      console.log('🔐 API: Token refresh failed, redirecting to login');
      await authService.logout();
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// Location API
export const locationAPI = {
  // Get all locations
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters);
    return api.get(`/locations?${params}`);
  },

  // Get single location
  getById: (id: string) => {
    return api.get(`/locations/${id}`);
  },

  // Create location
  create: (data: any) => {
    return api.post('/locations', data);
  },

  // Update location
  update: (id: string, data: any) => {
    return api.put(`/locations/${id}`, data);
  },

  // Delete location
  delete: (id: string) => {
    return api.delete(`/locations/${id}`);
  },

  // Get location statistics
  getStatistics: (id: string, period: string = 'today') => {
    return api.get(`/locations/${id}/statistics?period=${period}`);
  }
};

// Queue API
export const queueAPI = {
  // Get all queues
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters);
    return api.get(`/queues?${params}`);
  },

  // Get single queue
  getById: (id: string) => {
    return api.get(`/queues/${id}`);
  },

  // Create queue
  create: (data: any) => {
    return api.post('/queues', data);
  },

  // Update queue
  update: (id: string, data: any) => {
    return api.put(`/queues/${id}`, data);
  },

  // Delete queue
  delete: (id: string) => {
    return api.delete(`/queues/${id}`);
  },

  // Add SIP number to queue
  addSipNumber: (queueId: string, sipNumberId: string) => {
    return api.post(`/queues/${queueId}/sip-numbers`, { sipNumberId });
  },

  // Remove SIP number from queue
  removeSipNumber: (queueId: string, sipNumberId: string) => {
    return api.delete(`/queues/${queueId}/sip-numbers/${sipNumberId}`);
  },

  // Add agent to queue
  addAgent: (queueId: string, agentId: string) => {
    return api.post(`/queues/${queueId}/agents`, { agentId });
  },

  // Remove agent from queue
  removeAgent: (queueId: string, agentId: string) => {
    return api.delete(`/queues/${queueId}/agents/${agentId}`);
  },

  // Get queue statistics
  getStatistics: (id: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return api.get(`/queues/${id}/stats?${params}`);
  }
};

// SIP Number API
export const sipNumberAPI = {
  // Get all SIP numbers
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters);
    return api.get(`/sip?${params}`);
  },

  // Get single SIP number
  getById: (id: string) => {
    return api.get(`/sip/${id}`);
  },

  // Create SIP number
  create: (data: any) => {
    return api.post('/sip', data);
  },

  // Update SIP number
  update: (id: string, data: any) => {
    return api.put(`/sip/${id}`, data);
  },

  // Delete SIP number
  delete: (id: string) => {
    return api.delete(`/sip/${id}`);
  },

  // Update SIP number status
  updateStatus: (id: string, isOnline: boolean) => {
    return api.post(`/sip-numbers/${id}/status`, { isOnline });
  },

  // Get SIP number statistics
  getStatistics: (id: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return api.get(`/sip/${id}/stats?${params}`);
  }
};

// User API
export const userAPI = {
  // Get all users
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters);
    return api.get(`/users?${params}`);
  },

  // Get single user
  getById: (id: string) => {
    return api.get(`/users/${id}`);
  },

  // Create user
  create: (data: any) => {
    return api.post('/users', data);
  },

  // Update user
  update: (id: string, data: any) => {
    return api.put(`/users/${id}`, data);
  },

  // Delete user
  delete: (id: string) => {
    return api.delete(`/users/${id}`);
  },

  // Approve user (admin)
  approve: (id: string) => {
    return api.put(`/users/${id}/approve`);
  },

  // Update user status
  updateStatus: (id: string, status: string) => {
    return api.put(`/users/${id}/status`, { status });
  },

  // Update user queues
  updateQueues: (id: string, queueIds: string[]) => {
    return api.put(`/users/${id}/queues`, { queueIds });
  },

  // Get users by location
  getByLocation: (locationId: string) => {
    return api.get(`/users?locationId=${locationId}`);
  },

  // Get users by role
  getByRole: (role: string) => {
    return api.get(`/users?role=${role}`);
  },

  // Get locations
  getLocations: () => {
    return api.get('/locations');
  }
};

// Reports API
export const reportsAPI = {
  // Get daily statistics
  getDailyStats: (date?: string) => {
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    return api.get(`/reports/daily${qs}`);
  },

  // Get weekly statistics
  getWeeklyStats: (startDate: string, locationId?: string) => {
    const params = locationId ? `&locationId=${locationId}` : '';
    return api.get(`/reports/weekly?startDate=${startDate}${params}`);
  },

  // Get monthly statistics
  getMonthlyStats: (year: number, month: number, locationId?: string) => {
    const params = locationId ? `&locationId=${locationId}` : '';
    return api.get(`/reports/monthly?year=${year}&month=${month}${params}`);
  },

  // Get real-time statistics
  getRealtimeStats: () => {
    return api.get('/reports/realtime');
  },

  // Get advanced real-time statistics
  getAdvancedRealtimeStats: (locationId?: string) => {
    const params = locationId ? `?locationId=${locationId}` : '';
    return api.get(`/reports/advanced-realtime${params}`);
  },

  // Get active agents
  getActiveAgents: (locationId?: string) => {
    // Yeni agent-stats API'sini kullan
    const params = locationId ? `?locationId=${locationId}` : '';
    return api.get(`/agent-stats/all/today${params}`);
  },

  // Get waiting calls
  getWaitingCalls: (locationId?: string) => {
    const params = locationId ? `?locationId=${locationId}` : '';
    return api.get(`/reports/waiting-calls${params}`);
  },

  // Get queue performance
  getQueuePerformance: (startDate?: string, endDate?: string, locationId?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (locationId) params.append('locationId', locationId);
    const queryString = params.toString();
    return api.get(`/reports/queue-performance${queryString ? `?${queryString}` : ''}`);
  },

  // Get hourly distribution
  getHourlyDistribution: (date?: string) => {
    return api.get(`/reports/hourly-distribution?date=${date}`);
  },

  // Get agent performance
  getAgentPerformance: (agentId: string, startDate: string, endDate: string) => {
    return api.get(`/reports/agent-performance/${agentId}?startDate=${startDate}&endDate=${endDate}`);
  },

  // Get agent call statistics
  getAgentCallStats: (agentId: string, startDate: string, endDate: string) => {
    return api.get(`/reports/agent-call-stats/${agentId}?startDate=${startDate}&endDate=${endDate}`);
  },

  // Get agent status durations
  getAgentStatusDurations: (agentId: string, startDate: string, endDate: string) => {
    return api.get(`/reports/agent-status-durations/${agentId}?startDate=${startDate}&endDate=${endDate}`);
  },

  // Get all agents performance
  getAllAgentsPerformance: (startDate: string, endDate: string, locationId?: string) => {
    const params = locationId ? `&locationId=${locationId}` : '';
    return api.get(`/reports/all-agents-performance?startDate=${startDate}&endDate=${endDate}${params}`);
  },

  // Get agent statuses
  getAgentStatuses: () => {
    return api.get('/agent-statuses');
  },

  // Get agent status history for today
  getAgentStatusHistory: (agentId: string, date: string) => {
    return api.get(`/reports/agent-status-history/${agentId}?date=${date}`);
  },

  // Get queue performance details
  getQueuePerformanceDetails: (queueId: string, startDate: string, endDate: string) => {
    return api.get(`/reports/queue-performance/${queueId}?startDate=${startDate}&endDate=${endDate}`);
  },

  // Get daily breakdown
  getDailyBreakdown: (startDate: string, endDate: string, locationId?: string) => {
    const params = locationId ? `&locationId=${locationId}` : '';
    return api.get(`/reports/daily-breakdown?startDate=${startDate}&endDate=${endDate}${params}`);
  },

  // Get weekly breakdown
  getWeeklyBreakdown: (startDate: string, endDate: string, locationId?: string) => {
    const params = locationId ? `&locationId=${locationId}` : '';
    return api.get(`/reports/weekly-breakdown?startDate=${startDate}&endDate=${endDate}${params}`);
  },

  // Get period comparison
  getPeriodComparison: (
    period1Start: string, 
    period1End: string, 
    period2Start: string, 
    period2End: string, 
    locationId?: string
  ) => {
    const params = locationId ? `&locationId=${locationId}` : '';
    return api.get(
      `/reports/period-comparison?period1Start=${period1Start}&period1End=${period1End}&period2Start=${period2Start}&period2End=${period2End}${params}`
    );
  },

  // Export to CSV
  exportToCSV: (data: any, filename: string) => {
    return api.post('/reports/export/csv', { data, filename });
  },

  // Export to PDF
  exportToPDF: (data: any, filename: string) => {
    return api.post('/reports/export/pdf', { data, filename });
  }
};

// Auth API
export const authAPI = {
  // Register
  register: (userData: any) => {
    return api.post('/auth/register', userData);
  },

  // Login
  login: (credentials: any) => {
    console.log('🔐 API: Login request to /auth/login with:', credentials);
    return api.post('/auth/login', credentials);
  },

  // Logout
  logout: () => {
    return api.post('/auth/logout');
  },

  // Get current user
  getCurrentUser: () => {
    return api.get('/auth/me');
  },

  // Refresh token
  refreshToken: (data: any) => {
    return api.post('/auth/refresh', data);
  },

  // Change password
  changePassword: (data: any) => {
    return api.post('/auth/change-password', data);
  },

  // Revoke all tokens
  revokeAllTokens: () => {
    return api.post('/auth/revoke-all');
  },

  // Get user permissions
  getUserPermissions: () => {
    return api.get('/auth/permissions');
  },

  // Verify token
  verifyToken: () => {
    return api.post('/auth/verify');
  },

  // Signup
  signup: (formData: any) => {
    // Note: server-side route is /api/auth/register
    return api.post('/auth/register', formData);
  },
};

// Call API
export const callAPI = {
  // Get all calls
  getAll: (filters = {}) => {
    const params = new URLSearchParams(filters);
    return api.get(`/calls?${params}`);
  },

  // Get single call
  getById: (id: string) => {
    return api.get(`/calls/${id}`);
  },

  // Create call
  create: (data: any) => {
    return api.post('/calls', data);
  },

  // Update call
  update: (id: string, data: any) => {
    return api.put(`/calls/${id}`, data);
  },

  // Delete call
  delete: (id: string) => {
    return api.delete(`/calls/${id}`);
  },

  // Download call recording
  downloadRecording: (id: string) => {
    return api.get(`/calls/${id}/recording`, { responseType: 'blob' });
  },

  // Get active calls
  getActive: () => {
    return api.get('/calls/active');
  },

  // Get waiting calls
  getWaiting: () => {
    return api.get('/calls/waiting');
  },

  // Get agent call history
  getAgentCallHistory: (agentId: string, params: Record<string, any> = {}) => {
    const search = new URLSearchParams({ ...params, agentId });
    return api.get(`/calls?${search.toString()}`);
  },

  // Merge calls (Conference)
  mergeCalls: (callId1: string, callId2: string) => {
    return api.post('/calls/merge', { callId1, callId2 });
  }
};

// Messages API
export const messagesAPI = {
  // Get all users for recipient selection
  getUsers: () => {
    return api.get('/messages/users');
  },

  // Get conversations list
  getConversations: () => {
    return api.get('/messages/conversations');
  },

  // Get messages with a specific user
  getConversation: (userId: string) => {
    return api.get(`/messages/conversation/${userId}`);
  },

  // Send a message
  send: (receiverId: string, content: string) => {
    return api.post('/messages/send', { receiverId, content });
  },

  // Mark message as read
  markAsRead: (messageId: string) => {
    return api.put(`/messages/${messageId}/read`);
  },

  // Get unread message count
  getUnreadCount: () => {
    return api.get('/messages/unread/count');
  },

  // Delete message
  delete: (messageId: string) => {
    return api.delete(`/messages/${messageId}`);
  }
};

// ✨ Dialer API
export const dialerAPI = {
  // Save disposition for a dialer call
  saveDisposition: (dialerCallId: string, disposition: string, notes?: string, scheduleCallback?: { date: string; time: string }) => {
    return api.post(`/dialer/disposition/${dialerCallId}`, { disposition, notes, scheduleCallback });
  },
  
  // Schedule a callback
  scheduleCallback: (contactId: string, callbackDate: string, notes?: string) => {
    return api.post(`/dialer/schedule-callback/${contactId}`, { callbackDate, notes });
  }
};

export default api;
